import React, { useState, useRef } from "react";
import { Modal, ModalBody, Table, Badge, Button, Spinner } from "reactstrap";
import GeneralButton from "../button/GeneralButton";
import toast from "react-hot-toast";
import { uploadMadePCExcelFile } from "../../firebase/utility";
import { importMadePCProductsFromExcel } from "../../api/products.api";
import { IoCloseSharp } from "react-icons/io5";
import { 
  FiUploadCloud, 
  FiFileText, 
  FiCheckCircle, 
  FiAlertTriangle, 
  FiXCircle, 
  FiArrowLeft,
  FiImage,
  FiInfo
} from "react-icons/fi";
import Separator from "../Separator";

const EXPECTED_COLUMNS = [
  "Name",
  "SKU",
  "MPN",
  "Manufacturer ID",
  "SubCategory ID",
  "UPC",
  "Short Description",
  "Long Description",
  "Price",
  "Cost",
  "MSRP",
  "MAP",
  "Quantity",
  "Weight",
  "Shipping Dimension (LxWxH)",
  "Image",
  "Condition"
];

const ImportMadePCProductsModal = ({ isOpen, toggle, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState("");
  const [manualPath, setManualPath] = useState("");
  const [useManualPath, setUseManualPath] = useState(false);
  const [activeTab, setActiveTab] = useState("valid"); // 'valid' | 'invalid'
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setLoading(false);
    setImporting(false);
    setPreviewData({});
    setIsPreviewMode(false);
    setUploadedFilePath("");
    setManualPath("");
    setUseManualPath(false);
    setActiveTab("valid");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleToggle = () => {
    resetState();
    toggle();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUploadAndPreview = async () => {
    let targetPath = manualPath.trim();

    if (!useManualPath && !file) {
      toast.error("Please select an Excel file first.");
      return;
    }

    setLoading(true);
    try {
      if (!useManualPath) {
        const { path } = await uploadMadePCExcelFile(file);
        targetPath = path;
      }

      setUploadedFilePath(targetPath);

      importMadePCProductsFromExcel(targetPath, true, (res) => {
        setLoading(false);
        if (res?.status === 200 && res?.data) {
          setPreviewData(res.data?.data || res.data || {});
          setIsPreviewMode(true);
          toast.success("Made PC Products preview loaded successfully.");
        } else {
          toast.error(res?.message || "Failed to load Made PC products preview.");
        }
      });
    } catch (error) {
      toast.error("Error uploading file: " + (error?.message || error));
      setLoading(false);
    }
  };

  const handleFinalImport = () => {
    if (!uploadedFilePath) {
      toast.error("Missing file path for import.");
      return;
    }

    setImporting(true);
    importMadePCProductsFromExcel(uploadedFilePath, false, (res) => {
      setImporting(false);
      if (res?.status === 200) {
        toast.success(res?.data?.message || "Made PC Products imported successfully!");
        if (onSuccess) onSuccess();
        handleToggle();
      } else {
        toast.error(res?.message || "Failed to import Made PC products.");
      }
    });
  };

  const validRows = previewData?.validRows || previewData?.products || previewData?.validProducts || [];
  const invalidRows = previewData?.invalidRows || previewData?.errors || previewData?.invalidProducts || [];
  const totalProcessed = previewData?.totalProcessed || previewData?.totalRows || (validRows.length + invalidRows.length);
  const validCount = previewData?.validCount ?? validRows.length;
  const invalidCount = previewData?.invalidCount ?? invalidRows.length;

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="xl" className="modal-dialog-scrollable">
      <ModalBody className="p-4 bg-white">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h4 className="primary-color fw-bold mb-1">Import Made PC Products from Excel</h4>
            <p className="text-muted small mb-0">
              Preview or directly import Made PC catalog products, pricing, specifications, and images.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm rounded-circle p-1 d-flex align-items-center justify-content-center text-muted"
            onClick={handleToggle}
            style={{ width: "32px", height: "32px" }}
          >
            <IoCloseSharp size={20} />
          </button>
        </div>
        <Separator />

        <div className="mt-4">
          {!isPreviewMode ? (
            /* Upload View */
            <div className="row justify-content-center py-3">
              <div className="col-md-10">
                <div className="p-4 border rounded-3 text-center bg-light shadow-2xs">
                  <div className="bg-primary-soft text-primary d-inline-flex p-3 rounded-circle mb-3">
                    <FiUploadCloud size={36} />
                  </div>
                  <h5 className="fw-bold mb-2">Choose Made PC Excel Sheet</h5>
                  <p className="text-muted small mb-3">
                    Supports <code>.xlsx</code>, <code>.xls</code> or <code>.csv</code> files formatted for Made PC products.
                  </p>

                  {/* Expected Template Header Keys */}
                  <div className="bg-white p-3 rounded border text-start mb-4">
                    <div className="d-flex align-items-center gap-1 mb-2">
                      <FiInfo className="text-primary" size={14} />
                      <span className="fw-bold text-dark small">Expected Excel Template Columns (17 Keys):</span>
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {EXPECTED_COLUMNS.map((col, idx) => (
                        <span key={idx} className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "11px" }}>
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>

                  {!useManualPath ? (
                    <div className="mb-3">
                      <input
                        type="file"
                        className="form-control"
                        accept=".xlsx, .xls, .csv"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        disabled={loading || importing}
                      />
                      {file && (
                        <div className="d-flex align-items-center justify-content-center gap-2 mt-2 text-success small">
                          <FiFileText size={15} />
                          <span className="fw-medium">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-3 text-start">
                      <label className="form-label small fw-bold text-muted">Storage File Path</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. madepc/Made PC 20 Products.xlsx"
                        value={manualPath}
                        onChange={(e) => setManualPath(e.target.value)}
                        disabled={loading || importing}
                      />
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-decoration-none p-0 text-secondary"
                      onClick={() => setUseManualPath(!useManualPath)}
                      disabled={loading}
                    >
                      {useManualPath ? "← Upload file from computer" : "Use existing Storage Path →"}
                    </button>

                    <GeneralButton
                      title={loading ? "Uploading & Previewing..." : "Preview Products"}
                      className="blue-button mb-0"
                      width="220px"
                      loading={loading}
                      disabled={loading || (!useManualPath && !file) || (useManualPath && !manualPath.trim())}
                      handleClick={handleUploadAndPreview}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Preview View */
            <div>
              {/* Header Stats Bar */}
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2 border-bottom">
                <div>
                  <h5 className="fw-bold mb-0">Products Preview</h5>
                  <span className="text-muted small">
                    File: <code className="text-dark">{uploadedFilePath}</code> ({totalProcessed} total rows processed)
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    color="light"
                    size="sm"
                    className="border d-flex align-items-center gap-1"
                    onClick={() => setIsPreviewMode(false)}
                    disabled={importing}
                  >
                    <FiArrowLeft size={14} /> Back to Upload
                  </Button>
                </div>
              </div>

              {/* Status Counters */}
              <div className="d-flex gap-2 mb-3">
                <button
                  type="button"
                  className={`btn btn-sm px-3 py-1.5 d-flex align-items-center gap-2 rounded-2 ${
                    activeTab === "valid" ? "btn-success text-white" : "btn-outline-success"
                  }`}
                  onClick={() => setActiveTab("valid")}
                >
                  <FiCheckCircle size={14} />
                  <span>Valid Products ({validCount})</span>
                </button>
                {invalidCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm px-3 py-1.5 d-flex align-items-center gap-2 rounded-2 ${
                      activeTab === "invalid" ? "btn-danger text-white" : "btn-outline-danger"
                    }`}
                    onClick={() => setActiveTab("invalid")}
                  >
                    <FiXCircle size={14} />
                    <span>Invalid / Errors ({invalidCount})</span>
                  </button>
                )}
              </div>

              {/* Valid Products Preview Table */}
              {activeTab === "valid" && (
                <div style={{ maxHeight: "460px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                  <Table striped hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: "12px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 2 }}>
                      <tr className="text-secondary small">
                        <th style={{ width: "50px" }}>Row</th>
                        <th style={{ width: "60px" }}>Image</th>
                        <th>SKU / MPN</th>
                        <th>Name & Description</th>
                        <th>Mfg / SubCategory</th>
                        <th>Pricing (Price/Cost/MSRP/MAP)</th>
                        <th>Stock & Condition</th>
                        <th>Weight & Dim</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.length > 0 ? (
                        validRows.map((item, index) => {
                          const p = item?.updatePayload || item?.product || item;
                          
                          // Extract exact keys
                          const name = p["Name"] || p.name || p.title || item?.title || "—";
                          const sku = p["SKU"] || p.sku || item?.sku || "N/A";
                          const mpn = p["MPN"] || p.mpn || p.item_mfg_part || "—";
                          const upc = p["UPC"] || p.upc || p.item_upc || "";
                          const mfgId = p["Manufacturer ID"] || p.manufacturerId || p.mfg_id || p.manufacturer?.name || p.manufacturer || "Made PC";
                          const subCatId = p["SubCategory ID"] || p.subCategoryId || p.subCategory?.name || p.subCategory || "—";
                          const shortDesc = p["Short Description"] || p.shortDescription || p.description || "";
                          const price = p["Price"] ?? p.price ?? 0;
                          const cost = p["Cost"] ?? p.cost ?? p.stc_cost ?? 0;
                          const msrp = p["MSRP"] ?? p.msrp ?? p.stc_MSRP ?? 0;
                          const map = p["MAP"] ?? p.map ?? p.stc_map ?? 0;
                          const quantity = p["Quantity"] ?? p.quantity ?? p.stc_quantity ?? 0;
                          const weight = p["Weight"] ?? p.weight ?? 0;
                          const dimension = p["Shipping Dimension (LxWxH)"] || p.shippingDimension || p.dimensions || "—";
                          const image = p["Image"] || p.image?.src || p.image || p.imageUrl || "";
                          const condition = p["Condition"] || p.condition || p.mfg_condition || "New";

                          return (
                            <tr key={index}>
                              <td className="text-muted small">{item?.rowNumber || index + 1}</td>
                              <td>
                                {image ? (
                                  <img
                                    src={image}
                                    alt={sku}
                                    style={{ width: "36px", height: "36px", objectFit: "contain", borderRadius: "4px", border: "1px solid #dee2e6" }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="bg-light text-muted d-flex align-items-center justify-content-center rounded" style={{ width: "36px", height: "36px" }}>
                                    <FiImage size={16} />
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="fw-bold font-monospace text-primary">{sku}</div>
                                {mpn && mpn !== "—" && <div className="text-muted small">MPN: {mpn}</div>}
                                {upc && <div className="text-muted small font-monospace">UPC: {upc}</div>}
                              </td>
                              <td style={{ maxWidth: "240px" }}>
                                <div className="fw-semibold text-dark text-truncate" title={name}>
                                  {name}
                                </div>
                                {shortDesc && (
                                  <div className="text-muted small text-truncate" title={shortDesc}>
                                    {shortDesc}
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="small"><strong>Mfg:</strong> {String(mfgId)}</div>
                                <div className="small text-muted"><strong>SubCat:</strong> {String(subCatId)}</div>
                              </td>
                              <td>
                                <div><strong className="text-success">${Number(price).toFixed(2)}</strong> <span className="text-muted small">(Price)</span></div>
                                {cost > 0 && <div className="small text-muted">Cost: ${Number(cost).toFixed(2)}</div>}
                                {msrp > 0 && <div className="small text-muted">MSRP: ${Number(msrp).toFixed(2)}</div>}
                                {map > 0 && <div className="small text-muted">MAP: ${Number(map).toFixed(2)}</div>}
                              </td>
                              <td>
                                <Badge color={quantity > 0 ? "success" : "danger"} pill className="px-2 py-1">
                                  {quantity} in Stock
                                </Badge>
                                <div className="small text-muted mt-1">{condition}</div>
                              </td>
                              <td>
                                <div className="small">{weight ? `${weight} lbs` : "—"}</div>
                                <div className="small text-muted">{typeof dimension === "object" ? `${dimension.length || 0}x${dimension.width || 0}x${dimension.height || 0}` : dimension}</div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            No valid products found in this sheet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Invalid Rows Table */}
              {activeTab === "invalid" && (
                <div style={{ maxHeight: "440px", overflow: "auto", border: "1px solid #fed7d7", borderRadius: "6px" }}>
                  <Table striped hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: "13px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#fff5f5", zIndex: 2 }}>
                      <tr className="text-danger small">
                        <th style={{ width: "80px" }}>Row</th>
                        <th>SKU / Identifier</th>
                        <th>Error Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="fw-bold">{row?.rowNumber || idx + 1}</td>
                          <td className="font-monospace">{row?.sku || row?.identifier || row?.SKU || "N/A"}</td>
                          <td className="text-danger">
                            <FiAlertTriangle size={13} className="me-1" />
                            {Array.isArray(row?.errors) ? row.errors.join(", ") : (row?.error || row?.message || "Validation failed")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Bottom Actions */}
              <div className="mt-4 pt-2 border-top d-flex justify-content-between align-items-center">
                <span className="text-muted small">
                  Ready to upload and import <strong className="text-dark">{validCount}</strong> Made PC products into your catalog.
                </span>
                <GeneralButton
                  title={importing ? "Uploading & Importing..." : `Upload & Import Products (${validCount})`}
                  className="blue-button"
                  width="280px"
                  loading={importing}
                  disabled={importing || validCount === 0}
                  handleClick={handleFinalImport}
                />
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImportMadePCProductsModal;
